<template>
  <div class="chart-item">
    <div class="wrapper">
      <div class="tool-tip" v-if="toolTip" :data-tooltip="toolTip">
        <rp-sprite class="" name="help"/>
      </div>
      <div class="chart-wrapper">
        <router-link :to="(isLink && canLink) ? {name: getName, params: {'report_id': data.id}} : ''"
                     :class="!(isLink && canLink) ? 'no-link' : ''">
          <component
            :is="getComponent"
            :isDysphotopsia="isDysphotopsia"
            :progress="currentValue"
            :previous="previousValue"
            :refraction="refraction"
            :previousRefraction="previousRefraction"
            :textValue="getName"
            :numberVal="numberVal"
            :hidePercent="isCatprom || isCatquest"
            :colorOverride="colorOverride"
            :percentColorOverride="percentColorOverride" />
        </router-link>
        <div v-if="getName !== 'Additional procedures'" class="chart-title">
          <h4>{{getName}}</h4>
          <div v-if="activePatients" class="active">
            <div class="active-line"></div>
            <div class="active-percent">
              ACTIVE ({{calcActive}}%)
            </div>
          </div>
        </div>
        <div v-if="canClose" class="close-button" @click="onClose()">
          <rp-sprite class="" name="close" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import RpSprite from 'raypro_ui/src/components/ui/Sprite'
  import RangeMap from 'raypro_ui/src/components/ui/charts/PieProgressChart'
  import MultipleAnswers from 'raypro_ui/src/components/ui/charts/MultipleAnswers'
  import NumberChart from 'raypro_ui/src/components/ui/charts/NumberChart'
  import TextChart from 'raypro_ui/src/components/ui/charts/TextChart'
  import PatientNumbers from 'raypro_ui/src/components/ui/charts/PieNumberProgressChart'
  import {chartCalcs} from 'raypro_ui/src/mixins/chartCalcs'

  export default {
    mixins: [chartCalcs],
    components: {
      RpSprite,
      'AdminRangeMap': RangeMap,
      RangeMap,
      'AdminBooleanAnswers': TextChart,
      'BooleanAnswers': TextChart,
      'AdminMultipleAnswers': MultipleAnswers,
      MultipleAnswers,
      'AdminIOLNumbers': NumberChart,
      'IOLNumbers': NumberChart,
      'AdminPatientNumbers': PatientNumbers,
      PatientNumbers,
      'AdminUserNumbers': PatientNumbers,
      'Catprom5': RangeMap,
      'AdminCatprom5': RangeMap,
      'Catquest9': RangeMap,
      'AdminCatquest9': RangeMap,
    },
    props: {
      data: {type: [Object, Array], required: true},
      isLink: {type: Boolean, default: true},
      toolTip: {type: String, default: ''},
      canClose: {type: Boolean, default: false},
      colorOverride: {type: String, default: ''},
      percentColorOverride: {type: String, default: ''},
    },
    data() {
      return {
        charts: [
          'RangeMap',
          'MultipleAnswers',
          'NumberChart',
          'PatientNumbers',
          'BooleanAnswers',
          'IOLNumbers',
          'TextChart',
          'RingedNumberChart',
        ],
        noResult: false
      }
    },
    methods: {
      calculatePercentage(period) {
        if(this.data[period] === null) {
          return
        }
        if(Array.isArray(this.data)) {
          if(this.data[0].slug === 'spectacle_independence' ||
             this.data[0].slug === 'satisfaction_with_outcomes') {
            return this.withCategoryCalc(this.data, period)
          }
          if(this.data[0].slug === 'dysphotopsia') {
            return this.dysphotopsia(this.data, period)
            //return this.withCategoryCalc(this.data, period)
          }
          if(this.data[0].slug === 'additional_procedures') {
            return this.booleanAnswers(this.data)
          }
        }

        const scores = this.data[period].data
        const reportModule = this.data.implementation_module

        // TODO: THIS SHOULD BE HANDLED BY THE BACK
        if(Object.values(scores).every((val) => { return val === 0 })) {
          return 'N/A'
        }

        if(this.data[period].data.error) {
          return 'N/A'
        }

        if(reportModule === 'RangeMap' || reportModule === 'AdminRangeMap') {
          return this.rangeMap(scores)
        }

        return 0
      },

      onClose() {
        this.$emit('close')
      },
    },

    computed: {
      canLink() {
        const report = (Array.isArray(this.data)) ? this.data[0] : this.data
        return report.slug !== '' && !report.slug.startsWith('%')
      },

      getName() {
        if(Array.isArray(this.data)) {
          return this.data[0].name
        }
        return this.data.name
      },

      isDysphotopsia() {
        if(Array.isArray(this.data)) {
          if(this.data[0].slug === 'dysphotopsia') {
            return true
          }
        }
      },

      getComponent() {
        if(Array.isArray(this.data)) {
          return this.data[0].implementation_module
        }
        return this.data.implementation_module
      },

      currentValue() {
        if (this.isCatprom || this.isCatquest) {
          if (this.data.report_datas.data.error) {
            return 'N/A'
          }
          return this.data.report_datas.data.normalised_score
        }
        
        return this.calculatePercentage('report_datas')
      },

      previousValue() {
        let previousValue = this.calculatePercentage('previous_report_data')

        if(this.data.previous_report_data || Array.isArray(this.data)) {
          let dataArray = []

          if(Array.isArray(this.data)) {
            dataArray = this.data
          } else {
            dataArray.push(this.data)
          }

          for(let index = 0; index < dataArray.length; index++) {
            if(dataArray[index].previous_report_data) {
              if(dataArray[index].previous_report_data.data.error) {
                return 'under_10_answers'
              }
            }
          }
        }

        return typeof previousValue === 'number' && previousValue === this.currentValue ? ++previousValue : previousValue
      },

      calcActive() {
        const reportModule = this.data.implementation_module

        if(reportModule === 'PatientNumbers' || reportModule === 'AdminPatientNumbers' || reportModule === 'AdminUserNumbers') {
          return this.patientNumbers(this.data.report_datas.data)
        }
      },

      refraction() {
        if(!Array.isArray(this.data)) {
          const scores = this.data.report_datas.data

          return this.refractions(scores) || {}
        }
        return {}
      },

      previousRefraction() {
        if(!Array.isArray(this.data)) {
          if(this.data.previous_report_data === null) { return {} }
          const scores = this.data.previous_report_data.data
          let previousRefraction = this.refractions(scores)

          if(typeof previousRefraction === 'number' && previousRefraction === this.refraction) {
            return ++previousRefraction
          }

          return previousRefraction || {}
        }
        return {}
      },

      numberVal() {
        if(!Array.isArray(this.data)) {
          const totalPatients = this.data.report_datas.data.total

          return totalPatients
        }
        return false
      },

      activePatients() {
        if(!Array.isArray(this.data)) {
          const active = this.data.report_datas.data.active

          return active
        }
        return false
      },

      isCatprom() {
        return (this.data.implementation_module === 'Catprom5' ||
          this.data.implementation_module === 'AdminCatprom5')
      },

      isCatquest() {
        return (this.data.implementation_module === 'Catquest9' ||
          this.data.implementation_module === 'AdminCatquest9')
      },
    }
  }
</script>

<style lang="sass">
  @import 'raypro_ui/src/styles/base/_variables.sass'
  @import 'raypro_ui/src/styles/helpers/_mixins.sass'
  @import '../../../stylesheets/helpers/mixins'

  .chart-item
    width: 100%
    max-width: 320px
    position: relative
    background-color: $color-panel-bg
    box-shadow: $color-shadow
    margin-bottom: 30px
    margin-right: 30px
    border-radius: 20px
    -moz-box-shadow: 1px 1px 5px rgba(0, 0, 0, 0.06)
    -webkit-box-shadow: 1px 1px 5px rgba(0, 0, 0, 0.06)
    box-shadow: 1px 1px 5px rgba(0, 0, 0, 0.06)

    @include media(screen and (min-width: 500px))
      flex-basis: calc(50% - 30px)

    @include media(screen and (min-width: 740px))
      flex-basis: calc(33% - 30px)

    @include media(screen and (min-width: 1000px))
      flex-basis: calc(25% - 30px)

    .wrapper
      padding: 20px
      display: flex
      max-width: 320px
      align-items: center
      flex-direction: column
      justify-content: center

      .chart-wrapper
        width: 100%
        flex: 1
        min-height: 0
        padding-top: 10px

        .no-link
          pointer-events: none

        .chart-title
          font-family: 'Stolzl'
          text-align: center
          position: relative
          left: 50%
          //width: 150px
          color: $color-text
          transform: translateX(-50%)

          h4
            font-size: 13px
            line-height: 1
            // text-align: center
            font-weight: $font-medium
            margin-bottom: 5px
            //@include media($media-small)
            //  font-size: 14px

          h5
            // text-align: center
            font-size: 13px
            font-weight: $font-regular
            color: $color-placeholder

          .active
            display: flex
            flex-direction: row
            justify-content: center
            align-content: center
            align-items: center

            .active-line
              width: 10px
              border-top: 3px solid #299940
              margin-right: 7px

            .active-percent
              font-size: 12px

              @include media($media-small)
                font-size: 10px


            @include media($media-small)
              font-size: 10px

        .close-button
          position: absolute
          bottom: 30px
          right: 0px

          .sprite
            position: relative

        .color-ring
          fill: #244A96

      .tool-tip
        position: static !important
        @include rp-tooltip(attr(data-tooltip))

      .sprite
        cursor: pointer
        position: absolute
        top: 10px
        right: 10px
        width: 20px
        height: 20px
        margin: 0 3px
        fill: $color-side-nav

        @include media($media-small)
          margin: 0 3px

        svg
          fill: $color-panel-bg
          background-color: pink

    //@include media($media-small)
    //  font-size: 60px

</style>



// WEBPACK FOOTER //
// src/components/pages/Dashboard/ChartItem_v2.vue